import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import { IconButton, Tooltip } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { useGetCompanyInfo } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetCompanyInfo";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { downloadPdfThunk } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/download-pdf/store/effects";
import { selectIsPdfDownloading } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/download-pdf/store/selectors";
import { useThreadCreatedDate } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useThreadCreatedDate";
import { PdfExportService } from "app/services/PdfExportService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type DownloadButtonProps = {
    messageId: string;
};

export const DownloadButton = ({ messageId }: DownloadButtonProps) => {
    const dispatch = useDispatch();
    const isPdfLoading = useSelector((state) => selectIsPdfDownloading(state, messageId));
    const {
        params: { domain, id },
    } = useCurrentStateAndParams<{ domain?: string; id?: string }>();
    const threadCreatedDate = useThreadCreatedDate(id);

    const { data: companyInfo } = useGetCompanyInfo(domain ?? "");

    const pdfDownloadsMethod = () => {
        const date = dayjs(threadCreatedDate);
        return PdfExportService.downloadPaidPdfFedService(
            "si.pages.ai_agent.download_pdf.link",
            "si.pages.ai_agent.download_pdf.meeting.title",
            {
                overrideParams: {
                    messageId,
                },
                linkHref: `${swSettings.swsites.pro}/${proNavigator.getStateUrl(proNavigator.current(), {})}`,
                domain: companyInfo?.companyName || domain,
                date: date.format("MMMM YYYY"),
                duration: `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=64`,
            },
        );
    };

    const onClickDownloadPdf = () => {
        AIAgentTrackingService.meeting.trackDownloadBrief();
        if (!messageId) {
            return;
        }
        void dispatch(downloadPdfThunk(messageId, pdfDownloadsMethod));
    };

    return (
        <Tooltip content={translate("si.common.download-pdf")}>
            <IconButton
                iconName="pdf"
                type="flat"
                isDisabled={isPdfLoading}
                isLoading={isPdfLoading}
                onClick={onClickDownloadPdf}
                dataAutomation="download-button"
            >
                PDF
            </IconButton>
        </Tooltip>
    );
};
